/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { CalendarCheck, Download, IndianRupee, Smile, Users } from 'lucide-react';
import { type ChangeEvent, useCallback, useMemo, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { DonutChart, type DonutSlice } from '../../components/ui/donut-chart.js';
import { PageHeader } from '../../components/ui/page-header.js';
import { ProgressBar } from '../../components/ui/progress-bar.js';
import { QueryState } from '../../components/ui/query-state.js';
import { RevenueSessionsChart } from '../../components/ui/revenue-sessions-chart.js';
import { StatCard } from '../../components/ui/stat-card.js';
import {
  type AdminDashboard,
  type DistributionSlice,
  type MonthlyPoint,
  type TherapistPerf,
} from '../../lib/api/dashboard.js';
import { useAdminDashboard } from '../../lib/queries/dashboard.js';
import { downloadCsv, toCsv } from '../../lib/utils/csv.js';
import { initialsOf } from '../../lib/utils/initials.js';
import { formatInr } from '../../lib/utils/money.js';

import { AnalyticsReportSection } from './analytics-report.js';
import { DashboardCard } from './dashboard-card.js';

const RANGES = [3, 6, 12] as const;
type Range = (typeof RANGES)[number];

/** Toolbar: trailing-month range selector + CSV export of the current view. */
function AnalyticsToolbar({
  range,
  onRange,
  onExport,
}: {
  readonly range: Range;
  readonly onRange: (r: Range) => void;
  readonly onExport: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const onSelect = useCallback(
    (e: ChangeEvent<HTMLSelectElement>) => {
      onRange(Number(e.target.value) as Range);
    },
    [onRange],
  );
  return (
    <div className="flex flex-wrap items-center justify-end gap-3">
      <select
        aria-label={t('analytics.range')}
        value={range}
        onChange={onSelect}
        className="focus-ring h-10 rounded-xl border border-slate-200 bg-white px-3 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white"
      >
        {RANGES.map((r) => (
          <option key={r} value={r}>
            {t('analytics.lastMonths', { count: r })}
          </option>
        ))}
      </select>
      <button
        type="button"
        onClick={onExport}
        className="focus-ring inline-flex h-10 items-center gap-2 rounded-xl border border-slate-200 px-4 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
      >
        <Download className="h-4 w-4" aria-hidden="true" />
        {t('analytics.export')}
      </button>
    </div>
  );
}

const SLICE_COLORS = ['#5b47db', '#c084fc', '#10b981', '#f59e0b', '#0ea5e9', '#94a3b8'];
const NO_DATA_CLASS = 'py-6 text-center text-sm text-slate-400';

/** Four headline KPI tiles, derived from the server-computed dashboard stats. */
function AnalyticsKpis({ stats }: { readonly stats: AdminDashboard['stats'] }): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <StatCard
        icon={IndianRupee}
        label={t('admin.dashboard.totalRevenue')}
        value={formatInr(stats.revenuePaise)}
        accent="bg-[var(--accent)]"
        trend={stats.revenueTrendPct ?? undefined}
      />
      <StatCard
        icon={Users}
        label={t('admin.dashboard.activeClients')}
        value={String(stats.activeClients)}
        accent="bg-emerald-600"
        trend={stats.activeClientsTrendPct ?? undefined}
      />
      <StatCard
        icon={CalendarCheck}
        label={t('admin.dashboard.sessionsThisMonth')}
        value={String(stats.sessionsThisMonth)}
        accent="bg-orange-500"
        trend={stats.sessionsTrendPct ?? undefined}
      />
      <StatCard
        icon={Smile}
        label={t('admin.dashboard.avgSatisfaction')}
        value={stats.avgSatisfactionPct !== null ? `${String(stats.avgSatisfactionPct)}%` : '—'}
        accent="bg-[var(--accent)]"
        trend={stats.satisfactionTrendPct ?? undefined}
      />
    </div>
  );
}

/** Revenue Trend — revenue area + sessions line over the trailing 6 months. */
function RevenueTrendCard({ monthly }: { readonly monthly: readonly MonthlyPoint[] }): JSX.Element {
  const { t } = useTranslation();
  const data = monthly.map((m) => ({
    label: new Date(m.monthIso).toLocaleString('en', { month: 'short' }),
    revenue: Math.round(m.revenuePaise / 100),
    sessions: m.sessions,
  }));
  return (
    <DashboardCard title={t('analytics.revenueTrend')} subtitle={t('analytics.revenueTrendSub')}>
      <RevenueSessionsChart
        data={data}
        revenueName={t('admin.dashboard.revenue')}
        sessionsName={t('admin.dashboard.sessions')}
      />
    </DashboardCard>
  );
}

/** Diagnosis Distribution — donut by primary diagnosis with a counted legend. */
function DiagnosisCard({
  distribution,
}: {
  readonly distribution: readonly DistributionSlice[];
}): JSX.Element {
  const { t } = useTranslation();
  const slices: DonutSlice[] = distribution.map((d, i) => ({
    label: d.label,
    value: d.count,
    color: SLICE_COLORS[i % SLICE_COLORS.length] ?? '#94a3b8',
  }));
  return (
    <DashboardCard
      title={t('analytics.diagnosisDistribution')}
      subtitle={t('analytics.diagnosisSub')}
    >
      {slices.length === 0 ? (
        <p className={NO_DATA_CLASS}>{t('analytics.noData')}</p>
      ) : (
        <div>
          <DonutChart slices={slices} />
          <ul className="mt-4 space-y-2">
            {slices.map((s) => (
              <li key={s.label} className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
                  <span
                    className="h-2.5 w-2.5 rounded-full"
                    style={{ backgroundColor: s.color }}
                    aria-hidden="true"
                  />
                  {s.label}
                </span>
                <span className="font-medium text-slate-700 dark:text-slate-200">{s.value}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </DashboardCard>
  );
}

function UtilizationRow({
  row,
  maxSessions,
}: {
  readonly row: TherapistPerf;
  readonly maxSessions: number;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <li className="flex items-center gap-3">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--accent)] text-xs font-bold text-white">
        {initialsOf(row.name)}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between">
          <p className="truncate text-sm font-medium text-slate-800 dark:text-slate-100">
            {row.name}
          </p>
          <span className="shrink-0 text-xs text-slate-400">
            {t('admin.dashboard.sessionsUnit', { count: row.sessions })}
          </span>
        </div>
        <div className="mt-1.5">
          <ProgressBar value={maxSessions > 0 ? (row.sessions / maxSessions) * 100 : 0} />
        </div>
      </div>
    </li>
  );
}

/** Therapist Utilization — completed sessions per therapist as horizontal bars. */
function UtilizationCard({ rows }: { readonly rows: readonly TherapistPerf[] }): JSX.Element {
  const { t } = useTranslation();
  const maxSessions = Math.max(...rows.map((r) => r.sessions), 1);
  return (
    <DashboardCard
      title={t('analytics.therapistUtilization')}
      subtitle={t('analytics.utilizationSub')}
    >
      {rows.length === 0 ? (
        <p className={NO_DATA_CLASS}>{t('analytics.noData')}</p>
      ) : (
        <ul className="space-y-4">
          {rows.map((row) => (
            <UtilizationRow key={row.id} row={row} maxSessions={maxSessions} />
          ))}
        </ul>
      )}
    </DashboardCard>
  );
}

/** Admin → Analytics (Figma). Live, server-computed clinic analytics. */
export function AdminAnalyticsPage(): JSX.Element {
  const { t } = useTranslation();
  const dashboard = useAdminDashboard();
  const [range, setRange] = useState<Range>(6);
  const data = dashboard.data;

  const monthly = useMemo(() => (data?.monthly ?? []).slice(-range), [data?.monthly, range]);

  const onExport = useCallback(() => {
    if (data === undefined) {
      return;
    }
    const trend = toCsv(
      ['Month', 'Revenue (INR)', 'Sessions'],
      monthly.map((m) => [m.monthIso.slice(0, 7), (m.revenuePaise / 100).toFixed(2), m.sessions]),
    );
    const perf = toCsv(
      ['Therapist', 'Sessions', 'Revenue (INR)'],
      data.therapistPerformance.map((p) => [p.name, p.sessions, (p.revenuePaise / 100).toFixed(2)]),
    );
    downloadCsv('analytics.csv', `Revenue Trend\n${trend}\n\nTherapist Utilization\n${perf}`);
  }, [data, monthly]);

  return (
    <div className="p-8">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <PageHeader title={t('analytics.title')} subtitle={t('analytics.subtitle')} />
        <AnalyticsToolbar range={range} onRange={setRange} onExport={onExport} />
      </div>

      <QueryState
        isLoading={dashboard.isLoading}
        isError={dashboard.isError}
        isEmpty={false}
        loadingLabel={t('common.loading')}
        errorLabel={t('common.loadError')}
        emptyLabel=""
      >
        {data !== undefined && (
          <div className="space-y-6">
            <AnalyticsKpis stats={data.stats} />
            <RevenueTrendCard monthly={monthly} />
            <div className="grid gap-6 lg:grid-cols-2">
              <DiagnosisCard distribution={data.distribution} />
              <UtilizationCard rows={data.therapistPerformance} />
            </div>
          </div>
        )}
      </QueryState>

      <AnalyticsReportSection />
    </div>
  );
}
